import { apiClient } from '@/shared/api';
import type {
  AdminAttendance,
  AdminTeamAttendanceRecords,
  AggregateAttendanceItem,
  Attendance,
  ClockInResponse,
  ClockOutResponse,
  MonthlyCalendarResponse,
  PeriodSummaryResponse,
  TeamStatusResponse,
  AllTeamsStatusResponse,
  TeamOpsAttendanceBulkResponse,
  WeeklySummaryResponse,
} from '@aegislab/shared';

export type {
  ClockInResponse,
  ClockOutResponse,
  AdminAttendance,
  AdminTeamAttendanceRecords,
  AggregateAttendanceItem,
  TeamOpsAttendanceBulkResponse,
  PeriodSummaryResponse,
  WeeklySummaryResponse,
};

export const clockIn = async (): Promise<ClockInResponse> => {
  const response = await apiClient.post<ClockInResponse>('/attendance/clock-in');
  return response.data;
};

export const clockOut = async (id: string): Promise<ClockOutResponse> => {
  const response = await apiClient.post<ClockOutResponse>(`/attendance/${id}/clock-out`);
  return response.data;
};

export const startBreak = async (id: string): Promise<Attendance> => {
  const response = await apiClient.post<Attendance>(`/attendance/${id}/break/start`);
  return response.data;
};

export const endBreak = async (id: string): Promise<Attendance> => {
  const response = await apiClient.post<Attendance>(`/attendance/${id}/break/end`);
  return response.data;
};

// X3 단계 1d: PLAYER 본인 attendance (JWT에서 userId 추출)
// startDate·endDate 는 KST 날짜(YYYY-MM-DD) — 서버가 KST 경계로 clockInAt 을 좁힌다.
export const fetchMyAttendances = async (
  range: { startDate?: string; endDate?: string } = {}
): Promise<Attendance[]> => {
  const response = await apiClient.get<Attendance[]>('/attendance', {
    params: {
      ...(range.startDate ? { startDate: range.startDate } : {}),
      ...(range.endDate ? { endDate: range.endDate } : {}),
    },
  });
  return response.data;
};

// X3 단계 1d: admin scope, 다른 유저 attendance (userId query param)
export const fetchUserAttendances = async (userId: string): Promise<Attendance[]> => {
  const response = await apiClient.get<Attendance[]>('/attendance', {
    params: { userId },
  });
  return response.data;
};

// X3 단계 1a (D4·D8): admin scope read 흡수. 호출자 갱신은 단계 1d.
export const fetchAllAttendances = async (
  startDate?: string,
  endDate?: string
): Promise<AdminAttendance[]> => {
  const response = await apiClient.get<AdminAttendance[]>('/attendance/admin', {
    params: { ...(startDate && endDate ? { startDate, endDate } : {}) },
  });
  return response.data;
};

export const fetchTeamAttendances = async (teamId: string): Promise<AdminAttendance[]> => {
  const response = await apiClient.get<AdminAttendance[]>('/attendance/admin', {
    params: { teamId },
  });
  return response.data;
};

export const fetchAggregateAttendances = async (
  startDate: string,
  endDate: string
): Promise<AggregateAttendanceItem[]> => {
  const response = await apiClient.get<AggregateAttendanceItem[]>('/attendance/admin/aggregate', {
    params: { startDate, endDate },
  });
  return response.data;
};

/** 팀 출퇴근 기록(1일 1행 엑셀용). startDate·endDate 는 KST 날짜, endDate 포함. */
export const fetchAdminTeamRecords = async (
  teamId: string,
  startDate: string,
  endDate: string
): Promise<AdminTeamAttendanceRecords> => {
  const response = await apiClient.get<AdminTeamAttendanceRecords>('/attendance/admin/records', {
    params: { teamId, startDate, endDate },
  });
  return response.data;
};

export const resolveMissedClockOut = async (
  staleAttendanceId: string,
  clockOutTime: string
): Promise<Attendance> => {
  const response = await apiClient.post<Attendance>('/attendance/clock-in/resolve-missed', {
    staleAttendanceId,
    clockOutTime,
  });
  return response.data;
};

export const resolveMissedClockIn = async (
  clockInTime: string,
  clockOutTime: string
): Promise<Attendance> => {
  const response = await apiClient.post<Attendance>('/attendance/clock-out/resolve-missed', {
    clockInTime,
    clockOutTime,
  });
  return response.data;
};

export const fetchWeeklySummary = async (weekStart: string): Promise<WeeklySummaryResponse> => {
  const response = await apiClient.get<WeeklySummaryResponse>('/attendance/weekly-summary', {
    params: { weekStart },
  });
  return response.data;
};

/**
 * 고른 기간의 본인 합계 두 값 — 버그 제보 #119.
 * startDate·endDate 는 KST 날짜(YYYY-MM-DD). 주 임계 칸은 싣지 않는다(주 단위 선이라서).
 */
export const fetchPeriodSummary = async (
  startDate: string,
  endDate: string
): Promise<PeriodSummaryResponse> => {
  const response = await apiClient.get<PeriodSummaryResponse>('/attendance/period-summary', {
    params: { startDate, endDate },
  });
  return response.data;
};

export const fetchMonthlyCalendar = async (month: string): Promise<MonthlyCalendarResponse> => {
  const response = await apiClient.get<MonthlyCalendarResponse>('/attendance/calendar', {
    params: { month },
  });
  return response.data;
};

/** teamId 미지정 시 내 첫 소속 팀, 지정 시 그 팀 스코프(소속 팀만 — 비멤버는 403) */
export const fetchTeamStatus = async (teamId?: string): Promise<TeamStatusResponse> => {
  const response = await apiClient.get<TeamStatusResponse>('/attendance/team-status', {
    params: teamId ? { teamId } : undefined,
  });
  return response.data;
};

export const fetchTeamStatusBulk = async (
  teamIds: readonly string[]
): Promise<TeamOpsAttendanceBulkResponse> => {
  const response = await apiClient.get<TeamOpsAttendanceBulkResponse>(
    '/attendance/team-status-bulk',
    {
      params: { teamIds: teamIds.join(',') },
    }
  );
  return response.data;
};

export const fetchAllTeamsStatus = async (): Promise<AllTeamsStatusResponse> => {
  const response = await apiClient.get<AllTeamsStatusResponse>('/attendance/all-teams-status');
  return response.data;
};

export const attendanceApi = {
  clockIn,
  clockOut,
  startBreak,
  endBreak,
  fetchMyAttendances,
  fetchUserAttendances,
  fetchAllAttendances,
  fetchTeamAttendances,
  fetchAggregateAttendances,
  fetchAdminTeamRecords,
  resolveMissedClockOut,
  resolveMissedClockIn,
  fetchMonthlyCalendar,
  fetchTeamStatus,
  fetchTeamStatusBulk,
  fetchAllTeamsStatus,
};
